import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import SEO from "@/components/seo/SEO";
import { GoldButton, GhostButton, Eyebrow, SectionHeading, AmbientOrbs, GlassCard } from "@/components/common/ui";
import { Bed, Sparkle, ForkKnife, Robot, ChartLineUp, CheckCircle, Plugs } from "@phosphor-icons/react";
import { useEffect } from "react";

const ICONS = { reception: Bed, housekeeping: Sparkle, restaurant: ForkKnife, "ai-copilot": Robot, analytics: ChartLineUp };

const MODULE_DETAILS = {
  reception: {
    en: {
      capabilities: [
        "AI-assisted check-in in under 30 seconds",
        "Pre-arrival flow with document parsing and digital signature",
        "Unified guest profile — preferences, history, loyalty, open issues",
        "Mobile keys via Apple Wallet, Google Wallet or QR",
        "Real-time room availability from any connected PMS",
      ],
      integrations: ["Mews", "Apaleo", "Clock PMS", "SiteMinder", "Booking.com", "Assa Abloy"],
      kpis: [{ v: "70%", k: "Faster check-in" }, { v: "3.4x", k: "Upsell conversion" }, { v: "-40%", k: "Front-desk queues" }],
      faq: [
        { q: "Does BHT One replace my PMS?", a: "No — Reception is designed to sit next to your PMS. Deep integrations with Mews, Apaleo and Clock let you keep your PMS while modernising the guest experience." },
        { q: "How is guest ID data handled?", a: "IDs are parsed on-device, encrypted in transit and at rest, stored only for the legally required retention period, and never used for AI training." },
      ],
    },
    nl: {
      capabilities: [
        "AI-check-in in minder dan 30 seconden",
        "Pre-arrival flow met documentherkenning en digitale ondertekening",
        "Unified guest profile — voorkeuren, historie, loyalty, openstaande punten",
        "Mobile keys via Apple Wallet, Google Wallet of QR",
        "Real-time kamerbeschikbaarheid vanuit elke gekoppelde PMS",
      ],
      integrations: ["Mews", "Apaleo", "Clock PMS", "SiteMinder", "Booking.com", "Assa Abloy"],
      kpis: [{ v: "70%", k: "Snellere check-in" }, { v: "3,4x", k: "Upsell-conversie" }, { v: "-40%", k: "Balie-wachtrijen" }],
      faq: [
        { q: "Vervangt BHT One mijn PMS?", a: "Nee — Receptie is ontworpen om náást uw PMS te draaien. Diepe integraties met Mews, Apaleo en Clock laten u uw PMS behouden terwijl u de gastbeleving moderniseert." },
        { q: "Hoe wordt ID-data van gasten verwerkt?", a: "ID's worden on-device verwerkt, versleuteld in transit en at rest, alleen wettelijk vereist bewaard en nooit voor AI-training gebruikt." },
      ],
    },
  },
  housekeeping: {
    en: {
      capabilities: [
        "Real-time room status across floors and buildings",
        "AI dispatch that routes attendants by proximity, priority and load",
        "Mobile app for room attendants — checklists, photos, maintenance handoff",
        "Automatic sync with front office on room-ready",
        "Predictive supply forecasting for linen, amenities and chemicals",
      ],
      integrations: ["Mews", "Apaleo", "Optii", "Amadeus HotSOS", "Ecolab"],
      kpis: [{ v: "22%", k: "Rooms/attendant/day" }, { v: "-31%", k: "Turn-time variance" }, { v: "99.4%", k: "SLA adherence" }],
      faq: [
        { q: "Do attendants need to be tech-savvy?", a: "No. The mobile app is designed for one-handed operation, uses large tap targets, and works in 30+ languages with icon-first UI." },
        { q: "How does AI dispatch avoid unfair loads?", a: "The dispatcher balances rooms across attendants using a configurable fairness policy — measured, transparent and adjustable per property." },
      ],
    },
    nl: {
      capabilities: [
        "Real-time kamerstatus over verdiepingen en gebouwen",
        "AI-dispatch die attendants routeert op basis van nabijheid, prioriteit en load",
        "Mobiele app voor room attendants — checklists, foto's, onderhouds-handoff",
        "Automatische sync met front office bij kamer-klaar",
        "Voorspellende supply-forecast voor linnen, amenities en chemicaliën",
      ],
      integrations: ["Mews", "Apaleo", "Optii", "Amadeus HotSOS", "Ecolab"],
      kpis: [{ v: "22%", k: "Kamers/attendant/dag" }, { v: "-31%", k: "Turn-time variantie" }, { v: "99,4%", k: "SLA-adherentie" }],
      faq: [
        { q: "Moeten attendants tech-savvy zijn?", a: "Nee. De mobiele app is ontworpen voor eenhandig gebruik, met grote tap targets en werkt in 30+ talen met een icon-first UI." },
        { q: "Hoe voorkomt AI-dispatch oneerlijke belasting?", a: "De dispatcher balanceert kamers over attendants met een configureerbare fairness-policy — meetbaar, transparant en per property aanpasbaar." },
      ],
    },
  },
  restaurant: {
    en: {
      capabilities: [
        "Cloud-native POS with tableside and QR ordering",
        "Kitchen Display System with prep-time optimisation",
        "Allergen intelligence that flags conflicts at order time",
        "Revenue-per-cover analytics with menu engineering",
        "Native charge-to-room across every connected PMS",
      ],
      integrations: ["Mews", "Apaleo", "Stripe", "Adyen", "TISSL"],
      kpis: [{ v: "8-12%", k: "F&B revenue lift" }, { v: "24%", k: "Faster turn time" }, { v: "0", k: "Allergen misses" }],
      faq: [
        { q: "Can it run offline?", a: "Yes — the terminal caches locally and reconciles on reconnect. No orders lost, no receipts missed." },
      ],
    },
    nl: {
      capabilities: [
        "Cloud-native POS met tableside en QR-ordering",
        "Kitchen Display System met prep-time optimalisatie",
        "Allergenen-intelligentie die conflicten op orderniveau flagt",
        "Revenue-per-cover analytics met menu engineering",
        "Native charge-to-room over elke gekoppelde PMS",
      ],
      integrations: ["Mews", "Apaleo", "Stripe", "Adyen", "TISSL"],
      kpis: [{ v: "8-12%", k: "F&B revenue-groei" }, { v: "24%", k: "Snellere turn time" }, { v: "0", k: "Allergenen-missers" }],
      faq: [{ q: "Werkt het offline?", a: "Ja — de terminal cachet lokaal en reconcilieert bij hervinden. Geen orders kwijt, geen bonnen gemist." }],
    },
  },
  "ai-copilot": {
    en: {
      capabilities: [
        "In-module AI assistant for every role",
        "Natural-language actions across reservations, housekeeping and F&B",
        "Grounded on your SOPs, brand standards and historical data",
        "Human-in-the-loop guardrails on any guest-visible action",
        "Fine-grained audit log for every AI action",
      ],
      integrations: ["Claude Sonnet 4.5", "OpenAI", "Gemini", "Microsoft 365"],
      kpis: [{ v: "42%", k: "Faster staff onboarding" }, { v: "3x", k: "Ops questions self-served" }, { v: "100%", k: "Auditable actions" }],
      faq: [{ q: "Which model powers Copilot?", a: "By default Claude Sonnet 4.5 with the option to route to OpenAI or Gemini per capability. You can bring your own model keys or use ours." }],
    },
    nl: {
      capabilities: [
        "In-module AI-assistant voor elke rol",
        "Natural-language acties over reserveringen, housekeeping en F&B",
        "Gegrond op uw SOP's, brand standards en historische data",
        "Human-in-the-loop guardrails op elke gastzichtbare actie",
        "Fijnmazige audit log voor elke AI-actie",
      ],
      integrations: ["Claude Sonnet 4.5", "OpenAI", "Gemini", "Microsoft 365"],
      kpis: [{ v: "42%", k: "Snellere onboarding" }, { v: "3x", k: "Zelfbediende ops-vragen" }, { v: "100%", k: "Auditbare acties" }],
      faq: [{ q: "Welk model draait Copilot?", a: "Standaard Claude Sonnet 4.5 met de optie om per capability te routeren naar OpenAI of Gemini. U kunt eigen model-keys meenemen of de onze gebruiken." }],
    },
  },
  analytics: {
    en: {
      capabilities: [
        "Cross-module dashboards: ops, revenue, guest experience",
        "ADR / RevPAR / TRevPAR forecasts with confidence intervals",
        "Cohort revenue analysis by channel, source and campaign",
        "Automatic anomaly detection with narrative explanations",
        "Executive digest — a weekly written summary of your property",
      ],
      integrations: ["Snowflake", "BigQuery", "Databricks", "Power BI", "Looker"],
      kpis: [{ v: "6.4pp", k: "RevPAR uplift" }, { v: "72h", k: "From data → decision" }, { v: "40+", k: "Pre-built KPIs" }],
      faq: [{ q: "Can I connect my own warehouse?", a: "Yes. BHT Analytics ships bi-directional connectors to Snowflake, BigQuery and Databricks, plus a semantic layer for Power BI and Looker." }],
    },
    nl: {
      capabilities: [
        "Cross-module dashboards: ops, revenue, guest experience",
        "ADR / RevPAR / TRevPAR forecasts met betrouwbaarheidsintervallen",
        "Cohort revenue-analyse per channel, bron en campagne",
        "Automatische anomalie-detectie met verhalende uitleg",
        "Executive digest — een wekelijkse geschreven samenvatting van uw property",
      ],
      integrations: ["Snowflake", "BigQuery", "Databricks", "Power BI", "Looker"],
      kpis: [{ v: "6,4pp", k: "RevPAR-uplift" }, { v: "72u", k: "Van data → beslissing" }, { v: "40+", k: "Vooraf gebouwde KPI's" }],
      faq: [{ q: "Kan ik mijn eigen warehouse koppelen?", a: "Ja. BHT Analytics levert bi-directionele connectors voor Snowflake, BigQuery en Databricks, plus een semantic layer voor Power BI en Looker." }],
    },
  },
};

export default function ModulePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const navigate = useNavigate();
  const modules = t("modules.list", { returnObjects: true }) || [];
  const meta = modules.find((m) => m.slug === slug);
  const detail = MODULE_DETAILS[slug]?.[lang];

  useEffect(() => {
    if (!meta || !detail) navigate(`/${lang}/modules`, { replace: true });
  }, [meta, detail, lang, navigate]);

  if (!meta || !detail) return null;

  const Icon = ICONS[slug] || Bed;
  const related = modules.filter((m) => m.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={`${meta.title} — BHT One`}
        path={`/modules/${slug}`}
        schema={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: `${meta.title} — BHT One`,
          brand: "BHT Solutions",
          description: meta.desc,
        }}
      />

      <section className="relative overflow-hidden py-24 lg:py-36">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Link to={`/${lang}/modules`} className="eyebrow inline-flex items-center gap-2 hover:text-bht-gold">
            <span>← </span>{t("nav.modules")}
          </Link>
          <div className="mt-8 flex items-start gap-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-bht-gold/40 bg-bht-navy2">
              <Icon size={28} weight="thin" className="text-bht-gold" />
            </div>
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl md:text-7xl"
                data-testid="module-title"
              >
                {meta.title}
              </motion.h1>
              <p className="mt-4 max-w-2xl text-lg text-white/65">{meta.desc}</p>
            </div>
          </div>
          <div className="mt-12 flex flex-wrap gap-4">
            <GoldButton to={`/${lang}/book-demo`} testId="module-cta-demo">{t("cta.primary")}</GoldButton>
            <GhostButton to={`/${lang}/platform`}>{t("nav.platform")} →</GhostButton>
          </div>
        </div>
      </section>

      <section className="relative border-y border-white/[0.06] py-24">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <Eyebrow>{t("modules.detail.capabilities")}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight sm:text-5xl">
              {lang === "nl" ? "Gebouwd om productief te zijn vanaf dag één." : "Built to be productive from day one."}
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-4">
            {detail.capabilities.map((c, i) => (
              <li key={i} className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-5" data-testid={`module-capability-${i}`}>
                <CheckCircle size={20} weight="regular" className="mt-1 shrink-0 text-bht-gold" />
                <span className="text-[15px] text-white/85">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="relative py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={t("modules.detail.kpis")} title={lang === "nl" ? "Impact die u kunt meten." : "Impact you can measure."} />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {detail.kpis.map((s, i) => (
              <GlassCard key={i} className="p-8" testId={`module-kpi-${i}`}>
                <div className="font-serif text-5xl leading-none tracking-tight text-bht-gold">{s.v}</div>
                <div className="eyebrow mt-4">{s.k}</div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      <section className="relative border-t border-white/[0.06] py-24">
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <Eyebrow>{t("modules.detail.integrations")}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl">
              {lang === "nl" ? "Werkt met wat u al draait." : "Plays with what you already run."}
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {detail.integrations.map((n) => (
              <div key={n} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-4">
                <Plugs size={18} weight="regular" className="text-bht-gold" />
                <span className="text-sm text-white/80">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <SectionHeading eyebrow={t("modules.detail.faq")} title={lang === "nl" ? "Veelgestelde vragen." : "Frequently asked."} />
          <div className="mt-12 space-y-3">
            {detail.faq.map((f, i) => (
              <details key={i} className="group rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4 open:bg-white/[0.03]" data-testid={`module-faq-${i}`}>
                <summary className="cursor-pointer list-none font-serif text-lg text-white marker:hidden [&::-webkit-details-marker]:hidden">
                  <span className="inline-block w-6 font-mono text-[11px] tracking-widest text-bht-gold">Q/{String(i + 1).padStart(2, "0")}</span>
                  {f.q}
                </summary>
                <p className="mt-3 pl-8 text-sm leading-relaxed text-white/60">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="relative border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHeading eyebrow={t("modules.detail.related")} title={lang === "nl" ? "Ook interessant" : "Also worth exploring"} />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {related.map((m) => {
              const RIcon = ICONS[m.slug] || Bed;
              return (
                <Link
                  key={m.slug}
                  to={`/${lang}/modules/${m.slug}`}
                  className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-colors hover:border-bht-gold/30"
                  data-testid={`module-related-${m.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <RIcon size={22} weight="thin" className="text-bht-gold" />
                    <span className="font-mono text-[10px] tracking-widest text-white/30">→</span>
                  </div>
                  <div className="mt-6 font-serif text-xl group-hover:text-bht-gold">{m.title}</div>
                  <p className="mt-2 text-sm text-white/55">{m.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
